import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, X } from 'lucide-react';

export const ModifierGroupsAndAddOns = ({
  formData,
  onModifierGroupsChange,
  onAddOnsChange,
}) => {
  // Modifier Groups handlers
  const addModifierGroup = () => {
    const newGroups = [
      ...formData.modifier_groups,
      {
        name: '',
        required: false,
        multi_select: false,
        options: [],
      },
    ];
    onModifierGroupsChange(newGroups);
  };

  const removeModifierGroup = (groupIndex) => {
    const newGroups = formData.modifier_groups.filter((_, i) => i !== groupIndex);
    onModifierGroupsChange(newGroups);
  };

  const updateModifierGroup = (groupIndex, field, value) => {
    const newGroups = [...formData.modifier_groups];
    newGroups[groupIndex][field] = value;
    onModifierGroupsChange(newGroups);
  };

  const addModifierOption = (groupIndex) => {
    const newGroups = [...formData.modifier_groups];
    newGroups[groupIndex].options.push({
      name: '',
      price: 0,
    });
    onModifierGroupsChange(newGroups);
  };

  const removeModifierOption = (groupIndex, optionIndex) => {
    const newGroups = [...formData.modifier_groups];
    newGroups[groupIndex].options = newGroups[groupIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    onModifierGroupsChange(newGroups);
  };

  const updateModifierOption = (groupIndex, optionIndex, field, value) => {
    const newGroups = [...formData.modifier_groups];
    newGroups[groupIndex].options[optionIndex][field] = value;
    onModifierGroupsChange(newGroups);
  };

  // Add-Ons handlers
  const addAddOn = () => {
    const newAddOns = [
      ...formData.add_ons,
      {
        name: '',
        price: 0,
      },
    ];
    onAddOnsChange(newAddOns);
  };

  const removeAddOn = (index) => {
    const newAddOns = formData.add_ons.filter((_, i) => i !== index);
    onAddOnsChange(newAddOns);
  };

  const updateAddOn = (index, field, value) => {
    const newAddOns = [...formData.add_ons];
    newAddOns[index][field] = value;
    onAddOnsChange(newAddOns);
  };

  return (
    <div className="space-y-6">
      {/* Modifier Groups */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Modifier Groups</h3>
          <Button type="button" size="sm" onClick={addModifierGroup}>
            <Plus size={16} className="mr-2" />
            Add Group
          </Button>
        </div>

        {formData.modifier_groups.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No modifier groups added yet</p>
        ) : (
          formData.modifier_groups.map((group, groupIndex) => (
            <Card key={`group-${groupIndex}`} className="mb-4">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-3">
                      <Input
                        placeholder="Group name (e.g., Spice Level)"
                        value={group.name}
                        onChange={(e) =>
                          updateModifierGroup(groupIndex, 'name', e.target.value)
                        }
                      />
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={group.required}
                            onChange={(e) =>
                              updateModifierGroup(
                                groupIndex,
                                'required',
                                e.target.checked
                              )
                            }
                          />
                          <span className="text-sm">Required</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={group.multi_select}
                            onChange={(e) =>
                              updateModifierGroup(
                                groupIndex,
                                'multi_select',
                                e.target.checked
                              )
                            }
                          />
                          <span className="text-sm">Multi-select</span>
                        </label>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeModifierGroup(groupIndex)}
                    >
                      <X size={16} />
                    </Button>
                  </div>

                  <div className="ml-4 space-y-2">
                    {group.options.map((option, optionIndex) => (
                      <div
                        key={`option-${groupIndex}-${optionIndex}`}
                        className="flex gap-2"
                      >
                        <Input
                          placeholder="Option name"
                          value={option.name}
                          onChange={(e) =>
                            updateModifierOption(
                              groupIndex,
                              optionIndex,
                              'name',
                              e.target.value
                            )
                          }
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={option.price}
                          onChange={(e) =>
                            updateModifierOption(
                              groupIndex,
                              optionIndex,
                              'price',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeModifierOption(groupIndex, optionIndex)}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addModifierOption(groupIndex)}
                    >
                      <Plus size={14} className="mr-2" />
                      Add Option
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add-Ons */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Add-Ons</h3>
          <Button type="button" size="sm" onClick={addAddOn}>
            <Plus size={16} className="mr-2" />
            Add Add-On
          </Button>
        </div>

        {formData.add_ons.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No add-ons added yet</p>
        ) : (
          <div className="space-y-2">
            {formData.add_ons.map((addOn, index) => (
              <div key={`addon-${index}`} className="flex gap-2">
                <Input
                  placeholder="Add-on name"
                  value={addOn.name}
                  onChange={(e) => updateAddOn(index, 'name', e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Price"
                  value={addOn.price}
                  onChange={(e) =>
                    updateAddOn(index, 'price', parseFloat(e.target.value) || 0)
                  }
                  className="w-32"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAddOn(index)}
                >
                  <X size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
